import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Folder } from '../lib/api.js';

/**
 * Device pairing + force-sync folder selection.
 *
 * The desktop client never handles Discord credentials: it shows a short code,
 * the user approves it here in an already-authenticated session, and the app
 * exchanges it for its own device-scoped tokens.
 */
export function LinkPage(): ReactNode {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get('code') ?? '');
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [devices, setDevices] = useState<Awaited<ReturnType<typeof api.listDevices>>['devices']>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [pins, setPins] = useState<Set<string>>(new Set());
  const [savingPins, setSavingPins] = useState(false);

  const reload = (): void => {
    void api.listDevices().then((r) => setDevices(r.devices)).catch(() => setDevices([]));
    void api.listFolders({ tree: true }).then((r) => setFolders(r.folders)).catch(() => setFolders([]));
    void api.getPins().then((r) => setPins(new Set(r.folderIds))).catch(() => setPins(new Set()));
  };

  useEffect(reload, []);

  const approve = async (): Promise<void> => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    try {
      const res = await api.approveDevice(trimmed);
      setResult({ ok: true, message: `"${res.deviceName}" is now linked to your account.` });
      setCode('');
      reload();
    } catch (err) {
      setResult({
        ok: false,
        message: err instanceof Error ? err.message : 'Could not link that device',
      });
    }
  };

  const togglePin = async (folderId: string): Promise<void> => {
    const next = new Set(pins);
    if (next.has(folderId)) next.delete(folderId);
    else next.add(folderId);
    setPins(next);

    setSavingPins(true);
    try {
      await api.setPins([...next]);
    } catch {
      // Roll back so the checkbox never claims a state the server rejected.
      setPins(pins);
    } finally {
      setSavingPins(false);
    }
  };

  return (
    <div className="body no-sidebar">
      <div className="main" style={{ maxWidth: 820, margin: '0 auto', width: '100%' }}>
        <h2>Devices &amp; sync</h2>

        <div className="panel">
          <h3>Link a desktop app</h3>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
            Install the desktop client, and it will show an eight-character code. Enter it here
            to connect it to your account.
          </p>
          {result && (
            <div className={`notice ${result.ok ? 'success' : 'error'}`}>{result.message}</div>
          )}
          <div className="flex">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void approve();
              }}
              placeholder="A1B2C3D4"
              maxLength={16}
              style={{ maxWidth: 220, fontFamily: 'monospace', letterSpacing: 2 }}
            />
            <button className="btn primary" onClick={() => void approve()} disabled={!code.trim()}>
              Link device
            </button>
          </div>
        </div>

        <div className="panel">
          <h3>Linked devices</h3>
          {devices.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>No devices linked yet.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sync folder</th>
                  <th style={{ width: 150 }}>Last seen</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.id}>
                    <td>{device.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                      {device.sync_root_path ?? 'not configured'}
                    </td>
                    <td>
                      {device.last_seen_at
                        ? new Date(device.last_seen_at).toLocaleString()
                        : 'never'}
                    </td>
                    <td>
                      <button
                        className="btn sm danger"
                        onClick={() => {
                          if (confirm(`Unlink "${device.name}"? It will stop syncing.`)) {
                            void api.removeDevice(device.id).then(reload);
                          }
                        }}
                      >
                        Unlink
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel">
          <h3>Always keep offline {savingPins && <span style={{ color: 'var(--text-dim)' }}>saving…</span>}</h3>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
            Everything in the archive appears in your sync folder without using disk space, and
            downloads the moment you open it. Pin a folder here to force it to download in full
            and stay available offline — useful before heading to a launch site with no signal.
          </p>
          {folders.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>No folders yet.</p>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {folders.map((folder) => (
                <label
                  key={folder.id}
                  className="facet-row"
                  style={{ cursor: 'pointer', justifyContent: 'flex-start', gap: 10 }}
                >
                  <input
                    type="checkbox"
                    checked={pins.has(folder.id)}
                    onChange={() => void togglePin(folder.id)}
                    style={{ width: 'auto' }}
                  />
                  <span className="facet-label" style={{ flex: 1 }}>
                    {folder.path}
                  </span>
                  <span className="count">{folder.asset_count ?? 0}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
